import { RemoteLogJsonClient } from '@cvent/nucleus-remote-log-client';
import httpLogPageLoadId from '@tools/httpLogPageLoadId';
import { deviceDetect } from 'react-device-detect';
import getConfig from 'next/config';
import { LoggerFactory } from '@cvent/logging/LoggerFactory';

// Intentional legacy logging support for libraries. Do not use @cvent/nucleus-logging otherwise.
// eslint-disable-next-line no-restricted-imports
import { setLogClient, ConsoleLogClient, setDebugLevel, DebugLevels } from '@cvent/nucleus-logging';

const { publicRuntimeConfig } = getConfig();

const LOG = LoggerFactory.create('legacyLogging');

type LoggerContext = {
  httpLogPageLoadId: string;
  httpLogRequestId?: string;

  environment?: string;
  accountMappingId?: string;
  eventId?: string;
  attendeeId?: string;

  browserInfo?: {
    fullVersion: string;
    name: string;
    os: string;
    version: string;
  };

  windowLocation?: string;
};

class NucleusLoggingRequestBuilder {
  private url: string | URL;

  private headers: Headers;

  private requestBuilderOptions: RequestInit = { headers: {} };

  constructor(url: string, options: RequestInit) {
    this.url = url;
    this.headers = new Headers();
    this.requestBuilderOptions = { ...options, headers: this.headers };
  }

  public header(headerKey: string, headerValue: unknown) {
    if (headerKey && headerValue) {
      this.headers.set(headerKey, String(headerValue));

      this.requestBuilderOptions = {
        ...this.requestBuilderOptions,
        headers: this.headers
      };
    }

    return this;
  }

  public body(body: string) {
    if (body) {
      this.requestBuilderOptions = {
        ...this.requestBuilderOptions,
        body
      };
    }

    return this;
  }

  public build() {
    return new Request(this.url, this.requestBuilderOptions);
  }
}

// Context is not available until app start but we want to initialize the logger early.
// This will allow us to mutate the context when it is available.
const loggerContext: LoggerContext = { httpLogPageLoadId };

if (publicRuntimeConfig.IS_DEV === 'true') {
  // During local development the logs should not be suppressed, so change the log client to a console
  // logger that will log to the browser console for easier debugging / development.
  const consoleLogger = new ConsoleLogClient();
  setLogClient(consoleLogger);
} else if (typeof window !== 'undefined') {
  try {
    const {
      browserName: name,
      engineVersion: fullVersion,
      osName: os,
      browserMajorVersion: version
    } = deviceDetect(undefined);

    loggerContext.browserInfo = {
      fullVersion,
      name,
      os,
      version
    };
  } catch (e) {
    LOG.debug('An error occurred getting the browser information', e);
  }

  // For PR builds, we need to send cookies with log requests
  // so the request can be routed correctly by the ALB
  const includeCredentials = /-pr-/i.test(publicRuntimeConfig.CLIENT_VERSION);

  // Using the RemoteLogJsonClient to improve log structure persisted to the backend.
  // This overwrites the default RemoteLogClient logger set via initializeLogging.
  setLogClient(
    new RemoteLogJsonClient({
      url: publicRuntimeConfig.LOG_ENDPOINT,
      maxMessagesPerInterval: 200,
      maxWaitMs: 5000,

      requestBuilder: new NucleusLoggingRequestBuilder(publicRuntimeConfig.LOG_ENDPOINT, {
        method: 'POST',
        cache: 'no-cache',
        credentials: includeCredentials ? 'include' : 'omit',
        mode: 'same-origin'
      }),

      // Client is not property typed
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      context: loggerContext,

      // Include window location for each message. Must be generated at log
      // time instead of upfront to capture correct location.
      contextMiddleware: (context: LoggerContext): LoggerContext => {
        return {
          ...context,
          windowLocation:
            // Encode the uri to prevent WAF rules triggering
            window && window.location ? encodeURIComponent(window.location.toString()) : 'no-location'
        };
      }
    })
  );

  setDebugLevel(DebugLevels[publicRuntimeConfig.LOG_LEVEL] ?? DebugLevels.DEBUG);
}

export const updateLegacyLoggerContext = ({ accountMappingId, eventId, attendeeId, httpLogRequestId }): void => {
  loggerContext.environment = publicRuntimeConfig.ENVIRONMENT_NAME;
  loggerContext['account_mapping.id'] = accountMappingId;
  loggerContext['core.event.id'] = eventId;
  loggerContext['core.event.attendee.id'] = attendeeId;
  loggerContext.httpLogRequestId = httpLogRequestId;
};
